import { ReactComponent as InforIcon } from "../../shared/assets/informationLogo.svg";
import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { api, apiUrl } from "../../shared/services/service";
import { generateUUID } from "../../shared/services/utils";
import { TechFormSectionLoadError } from "../components/TechFormSectionLoadError";
import { HeaderCell, InputCell, LabelCell } from "../components/Tq1InputCell";
import VesselContactDetails from "../components/VesselContactDetails";
import type { TechFormPageProps, TechFormFieldErrors } from "../models/tech-form-page.models";
import { TECH_FORM_ENDPOINTS } from "../models/tech-form-section-data.constants";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import {
  Tq1VesselDetailsM,
  contactDetailsDefaults,
  mapSqlToTq1VesselDetailsForm,
  vesselDetailsFormInit,
} from "../models/tq1-vessel-details.models";
import {
  invalidateTechFormSectionDataCacheForAllModes,
  isTechFormSectionDataCached,
  loadTechFormEndpointData,
} from "../services/tech-form-section-data.service";
import { tq1VesselDetailsAtom, tq3DraftAtom } from "../state/tech-form.atoms";
import { prepareVesselDetailsForEditing } from "../utils/prepare-vessel-details-for-editing.utils";
import { promoteOrInsertFirstEmailContact } from "../utils/promote-or-insert-first-email-contact.utils";
import {
  FAKE_VESSEL_IMO_NUMBER_WARNING,
  isFakeVesselImoNumberForTesting,
  sanitizeTechFormInputForField,
  validateTq1VesselDetails,
} from "../utils/tech-form-validations";
import {
  getAllWlthcFieldsDefined,
  getAllContactDetailsFieldsDefined,
} from "../utils/vessel-details-completeness.utils";
import { useAtom, useAtomValue } from "jotai";
import { set } from "lodash";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";
import { Paths } from "type-fest";

export {
  getAllWlthcFieldsDefined,
  getAllContactDetailsFieldsDefined,
} from "../utils/vessel-details-completeness.utils";

export type { TechFormFieldErrors as FormErrors } from "../models/tech-form-page.models";

export const Tq1VesselDetails: React.FC<TechFormPageProps> = ({
  isCompletedForm,
  cacheInitialForm,
  onSectionReady,
}) => {
  const theme = useAtomValue(themeAtom);
  const draftDetails = useAtomValue(tq3DraftAtom);

  const { imoNumber, vesselName } = useParams();
  const [storedForm, setStoredForm] = useAtom(tq1VesselDetailsAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [isSectionDataReady, setIsSectionDataReady] = useState(false);
  const [sectionLoadError, setSectionLoadError] = useState(false);
  const [sectionLoadAttempt, setSectionLoadAttempt] = useState(0);
  const [form, setForm] = useState<Tq1VesselDetailsM>(vesselDetailsFormInit(vesselName, imoNumber));
  const [formErrors, setFormErrors] = useState<TechFormFieldErrors<Tq1VesselDetailsM>>({});
  const [allWlthcFieldsDefined, setAllWlthcFieldsDefined] = useState(true);
  const [contactDetailsErrors, setContactDetailsErrors] = useState<Array<Record<string, boolean>>>([]);
  const fakeVesselImoNumberWarning = isFakeVesselImoNumberForTesting(form.imoNumber)
    ? FAKE_VESSEL_IMO_NUMBER_WARNING
    : "";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let ignoreResult = false;
    setSectionLoadError(false);
    const storedVesselDetailsHaveFormData = Object.keys(storedForm ?? {}).some(
      (key) => key !== "techFormSchemaVersion",
    );
    const storedVesselDetailsHaveSchemaMetadata = Object.prototype.hasOwnProperty.call(
      storedForm ?? {},
      "techFormSchemaVersion",
    );
    const _form = prepareVesselDetailsForEditing(storedForm, vesselName, imoNumber);

    if (isCompletedForm) {
      void fetchData(() => ignoreResult);
    } else {
      if (!storedVesselDetailsHaveFormData || !storedVesselDetailsHaveSchemaMetadata) {
        void fetchData(() => ignoreResult, storedVesselDetailsHaveFormData ? _form : undefined);
      } else {
        const _allWlthcFieldsDefined = getAllWlthcFieldsDefined(_form);
        const { contactErrors } = getAllContactDetailsFieldsDefined(_form);

        setAllWlthcFieldsDefined(_allWlthcFieldsDefined); // WLTHC validation
        setContactDetailsErrors(contactErrors); // Contact Details Validation

        setForm(_form);
        cacheInitialForm(_form);
        setIsSectionDataReady(true);
        onSectionReady?.();
      }
    }

    return () => {
      ignoreResult = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imoNumber, vesselName, isCompletedForm, sectionLoadAttempt]);

  useEffect(() => {
    setFormErrors(validateTq1VesselDetails(form) as TechFormFieldErrors<Tq1VesselDetailsM>);
    setContactDetailsErrors(getAllContactDetailsFieldsDefined(form).contactErrors);
    setAllWlthcFieldsDefined(getAllWlthcFieldsDefined(form));
  }, [form, draftDetails?.summerDWT]);

  const fetchData = async (isStale: () => boolean, cachedForm?: Tq1VesselDetailsM) => {
    if (!imoNumber || !vesselName) return;

    const requestIdentity = {
      imoNumber,
      vesselName,
      mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
    };
    if (!isTechFormSectionDataCached({ ...requestIdentity, section: 1 })) {
      setIsLoading(true);
    }

    let generalInfoData: any = [];
    let contactsData: any[] = [];

    try {
      const [generalInformationResult, contactsResult] = await Promise.allSettled([
        loadTechFormEndpointData<any[]>({
          ...requestIdentity,
          endpoint: TECH_FORM_ENDPOINTS.generalInformation,
        }),
        loadTechFormEndpointData<any[]>({ ...requestIdentity, endpoint: TECH_FORM_ENDPOINTS.contacts }),
      ]);

      if (generalInformationResult.status === "rejected") {
        throw generalInformationResult.reason;
      }
      generalInfoData = generalInformationResult.value[0];

      if (contactsResult.status === "fulfilled") {
        contactsData = contactsResult.value;
      } else {
        const error = contactsResult.reason;
        if (error.response?.status === 404) {
          console.warn("Contacts not found (404)");
        } else {
          console.error("Error fetching Contacts", error);
        }
      }

      if (isStale()) return;

      const fetchedVesselInfo = mapSqlToTq1VesselDetailsForm(generalInfoData, contactsData, vesselName, imoNumber);
      const vesselInfo = cachedForm
        ? { ...cachedForm, techFormSchemaVersion: fetchedVesselInfo.techFormSchemaVersion }
        : fetchedVesselInfo;

      const _allWlthcFieldsDefined = getAllWlthcFieldsDefined(vesselInfo);
      const { contactErrors } = getAllContactDetailsFieldsDefined(vesselInfo);

      setAllWlthcFieldsDefined(_allWlthcFieldsDefined); // Initial validation
      setContactDetailsErrors(contactErrors);
      setFormErrors(validateTq1VesselDetails(vesselInfo) as TechFormFieldErrors<Tq1VesselDetailsM>);

      setForm(vesselInfo);
      setStoredForm(vesselInfo);
      cacheInitialForm(vesselInfo);
      setIsSectionDataReady(true);
      onSectionReady?.();
    } catch (error) {
      if (!isStale()) setSectionLoadError(true);
      console.error("Error fetching data:", error);
    } finally {
      if (!isStale()) {
        setIsLoading(false);
      }
    }
  };

  // Storage
  useEffect(() => {
    if (isSectionDataReady && !isLoading) {
      setStoredForm({ ...form });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isCompletedForm, isLoading, isSectionDataReady]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    key: Paths<Tq1VesselDetailsM>,
  ) => {
    const { value, type } = e.target;
    const newValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : sanitizeTechFormInputForField(key, value);

    setForm((prevState) => {
      const newVdForm = set(structuredClone(prevState), key, newValue);
      const _allWlthcFieldsDefined = getAllWlthcFieldsDefined(newVdForm);
      const { contactErrors } = getAllContactDetailsFieldsDefined(newVdForm);

      setAllWlthcFieldsDefined(_allWlthcFieldsDefined);
      setContactDetailsErrors(contactErrors);
      setFormErrors(validateTq1VesselDetails(newVdForm) as TechFormFieldErrors<Tq1VesselDetailsM>);

      return newVdForm;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({
      vesselName: form.vesselName ? undefined : "This field is required",
      imoNumber: String(form.imoNumber ?? "").trim() ? undefined : "This field is required",
      ...(validateTq1VesselDetails(form) as TechFormFieldErrors<Tq1VesselDetailsM>),
    });
  };

  const addContactDetail = () => {
    const _contactDetails = [...form.contactDetails];

    if (_contactDetails.slice(1).length >= 6) return;

    const newContact = {
      ...contactDetailsDefaults[0],
      contactId: generateUUID(),
    };

    _contactDetails.push(newContact);

    setForm((prev) => ({
      ...prev,
      contactDetails: _contactDetails,
    }));

    // Validate all contact details including the new one
    const { contactErrors } = getAllContactDetailsFieldsDefined({
      ...form,
      contactDetails: _contactDetails,
    });

    setContactDetailsErrors(contactErrors);
  };

  const deleteContact = async (id: string) => {
    try {
      if (!imoNumber || !vesselName) return;

      await api.post(`${apiUrl}/tq_DelContact`, undefined, {
        params: { imo_no: imoNumber, vessel_name: vesselName, id: id },
      });
      invalidateTechFormSectionDataCacheForAllModes({ imoNumber, vesselName, section: 1 });
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn("Incident not found (404)");
      } else {
        console.error("Error deleting Incident", error);
      }
    }
  };

  const removeContactDetail = async (index: number) => {
    const id = form.contactDetails[index].contactId;

    try {
      await deleteContact(id);
    } catch (error) {
      console.error("Failed to delete contact from backend", error);
    }

    let _contacts = [...form.contactDetails];
    _contacts.splice(index, 1);

    if (_contacts.length === 0) {
      _contacts.push({
        ...contactDetailsDefaults[0],
        contactId: generateUUID(),
      });
    }

    const normalizedContacts = promoteOrInsertFirstEmailContact(_contacts);
    setForm((prev) => ({ ...prev, contactDetails: normalizedContacts }));

    // ✅ Validate updated contact list
    const { contactErrors } = getAllContactDetailsFieldsDefined({
      ...form,
      contactDetails: normalizedContacts,
    });

    setContactDetailsErrors(contactErrors);
  };

  if (sectionLoadError) {
    return <TechFormSectionLoadError onRetry={() => setSectionLoadAttempt((attempt) => attempt + 1)} />;
  }

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div
        className={twMerge(
          "p-2 md:p-4",
          "[&_input]:border",
          "[&_select]:text-sm",
          theme.home.tailwind.panelBackground,
          theme.home.tailwind.textDefault,
          theme.balticQuestionnaire.labelsText,
          theme.balticQuestionnaire.inputsBg,
          theme.balticQuestionnaire.inputsText,
          theme.balticQuestionnaire.selectsBg,
          theme.balticQuestionnaire.textsBg,
          theme.balticQuestionnaire.textsText,
          theme.balticQuestionnaire.textsBorder,
          theme.balticQuestionnaire.selectsScroller,
        )}
      >
        <h1 className="custom-heading">Vessel’s details</h1>
        <br />
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-5 gap-6">
            <div className="mb-4" style={{ order: 1 }}>
              <label className="block text-sm font-medium">Vessel Name</label>
              <input
                name="vesselName"
                type="text"
                value={form.vesselName}
                onChange={(e) => handleInputChange(e, "vesselName")}
                className={twMerge(
                  "mt-1 w-full rounded-md border p-2 focus:outline-none",
                  formErrors.vesselName ? "border-red-600" : theme.uwhcAssistant.border,
                )}
                disabled
              />
              {formErrors.vesselName && <p className="mt-1 text-xs text-red-600">{formErrors.vesselName}</p>}
            </div>
            <div className="mb-4" style={{ order: 2 }}>
              <label className="block text-sm font-medium">IMO Number</label>
              <input
                name="imoNumber"
                type="text"
                disabled
                value={form.imoNumber}
                onChange={(e) => handleInputChange(e, "imoNumber")}
                className={twMerge(
                  "mt-1 w-full rounded-md border p-2 focus:outline-none",
                  formErrors.imoNumber ? "border-red-600" : theme.uwhcAssistant.border,
                )}
              />
              {formErrors.imoNumber && <p className="mt-1 text-xs text-red-600">{formErrors.imoNumber}</p>}
              {fakeVesselImoNumberWarning && (
                <p
                  data-testid="tq1-vessel-details-fake-vessel-imo-warning"
                  className="mt-1 text-xs font-medium text-[#946200]"
                >
                  {fakeVesselImoNumberWarning}
                </p>
              )}
            </div>
            <div className="mb-4" style={{ order: 3 }}>
              <label className="block text-sm font-medium">GT/NT</label>
              <input
                name="gtNt"
                type="text"
                value={form.gtNt}
                onChange={(e) => handleInputChange(e, "gtNt")}
                className={twMerge(
                  "mt-1 w-full rounded-md border p-2 focus:outline-none",
                  formErrors.gtNt ? "border-red-600" : theme.uwhcAssistant.border,
                )}
              />
              {formErrors.gtNt && <p className="mt-1 text-xs text-red-600">{formErrors.gtNt}</p>}
            </div>
            <div className="mb-4" style={{ order: 26 }}>
              <label className="block text-sm font-medium">Max crane outreach</label>
              <div className="relative flex items-center">
                <input
                  name="maxCraneOutReach"
                  type="text"
                  value={form.maxCraneOutReach}
                  onChange={(e) => handleInputChange(e, "maxCraneOutReach")}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.maxCraneOutReach ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.maxCraneOutReach ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m
                </span>
              </div>
              {formErrors.maxCraneOutReach && (
                <p className="mt-1 text-xs text-red-600">{formErrors.maxCraneOutReach}</p>
              )}
            </div>
            <div className="mb-4" style={{ order: 4 }}>
              <label className="mb-3 block w-[300px] text-sm font-medium">
                Confirm Ship Has Right Handed Propeller
              </label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    name="ConfirmShipHasRightHandedPropeller"
                    type="radio"
                    className="form-radio"
                    value="yes"
                    checked={form.ConfirmShipHasRightHandedPropeller === "yes"}
                    onChange={(e) => handleInputChange(e, "ConfirmShipHasRightHandedPropeller")}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    name="ConfirmShipHasRightHandedPropeller"
                    type="radio"
                    className="form-radio"
                    value="no"
                    checked={form.ConfirmShipHasRightHandedPropeller === "no"}
                    onChange={(e) => handleInputChange(e, "ConfirmShipHasRightHandedPropeller")}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.ConfirmShipHasRightHandedPropeller && (
                <p className="mt-1 text-xs text-red-600">{formErrors.ConfirmShipHasRightHandedPropeller}</p>
              )}
            </div>
            <Tooltip
              id="wlthc"
              disableStyleInjection={true}
              place="top"
              style={{
                fontSize: "12px",
                color: "#fff",
                background: "#4A5655",
                borderRadius: "8px",
                position: "absolute",
                top: "0",
              }}
            />
            <div className="col-span-full flex flex-col gap-4" style={{ order: 10 }}>
              <VesselContactDetails
                form={form}
                contactDetailsErrors={contactDetailsErrors}
                handleInputChange={handleInputChange}
                addContactDetail={addContactDetail}
                removeContactDetail={removeContactDetail}
              />
            </div>
            <div className="mb-4" style={{ order: 24 }}>
              <label className="one-line-label">
                Height of vessel{" "}
                <span>
                  <InforIcon className="h-3 w-3" data-tooltip-id="my-tooltip-children-multiline" />
                </span>
              </label>
              <Tooltip
                id="my-tooltip-children-multiline"
                disableStyleInjection={true}
                style={{ color: "#fff", background: "#4A5655", borderRadius: "8px", position: "absolute", right: "0" }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <p>
                    Distance from keel to top mast
                    <br /> (highest point of the vessel)
                  </p>
                </div>
              </Tooltip>
              <div className="relative flex items-center">
                <input
                  name="heightOfVessel"
                  type="text"
                  value={form.heightOfVessel}
                  onChange={(e) => handleInputChange(e, "heightOfVessel")}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.heightOfVessel ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.heightOfVessel ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m
                </span>
              </div>
              {formErrors.heightOfVessel && <p className="mt-1 text-xs text-red-600">{formErrors.heightOfVessel}</p>}
            </div>
            <div className="mb-4" style={{ order: 20 }}>
              <label className="block text-sm font-medium">LOA</label>
              <div className="relative flex items-center">
                <input
                  name="loa"
                  type="text"
                  value={form.loa}
                  onChange={(e) => handleInputChange(e, "loa")}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.loa ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.loa ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m
                </span>
              </div>
              {formErrors.loa && <p className="mt-1 text-xs text-red-600">{formErrors.loa}</p>}
            </div>
            <div className="mb-4" style={{ order: 21 }}>
              <label className="block text-sm font-medium">LBP</label>
              <div className="relative flex items-center">
                <input
                  name="lbp"
                  type="text"
                  value={form.lbp}
                  onChange={(e) => handleInputChange(e, "lbp")}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.lbp ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.balticQuestionnaire.grayBg,
                    formErrors.lbp ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  m
                </span>
              </div>
              {formErrors.lbp && <p className="mt-1 text-xs text-red-600">{formErrors.lbp}</p>}
            </div>
            <div className="mb-4" style={{ order: 23 }}>
              <label className="block text-sm font-medium">Moulded Depth</label>
              <div className="relative flex items-center">
                <input
                  name="mouldedDepth"
                  type="text"
                  value={form.mouldedDepth}
                  onChange={(e) => handleInputChange(e, "mouldedDepth")}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.mouldedDepth ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.mouldedDepth ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m
                </span>
              </div>
              {formErrors.mouldedDepth && <p className="mt-1 text-xs text-red-600">{formErrors.mouldedDepth}</p>}
            </div>
            <div className="mb-4" style={{ order: 22 }}>
              <label className="block text-sm font-medium">Beam</label>
              <div className="relative flex items-center">
                <input
                  name="beam"
                  type="text"
                  value={form.beam}
                  onChange={(e) => handleInputChange(e, "beam")}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.beam ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.beam ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m
                </span>
              </div>
              {formErrors.beam && <p className="mt-1 text-xs text-red-600">{formErrors.beam}</p>}
            </div>
            <div className="mb-4" style={{ order: 28 }}>
              <label className="block text-sm font-medium">Lightweight</label>
              <div className="relative flex items-center">
                <input
                  name="lightWeight"
                  type="text"
                  value={form.lightWeight}
                  onChange={(e) => handleInputChange(e, "lightWeight")}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.lightWeight ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.balticQuestionnaire.grayBg,
                    formErrors.lightWeight ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  mt
                </span>
              </div>
              {formErrors.lightWeight && <p className="mt-1 text-xs text-red-600">{formErrors.lightWeight}</p>}
            </div>
            <div className="mb-4" style={{ order: 29 }}>
              <label className="block text-sm font-medium">Constant</label>
              <input
                name="constant"
                type="text"
                value={form.constant}
                onChange={(e) => handleInputChange(e, "constant")}
                className={twMerge(
                  "mt-1 w-full rounded-md border p-2 focus:outline-none",
                  formErrors.constant ? "border-red-600" : theme.uwhcAssistant.border,
                )}
              />
              {formErrors.constant && <p className="mt-1 text-xs text-red-600">{formErrors.constant}</p>}
            </div>
            <div className="mb-4" style={{ order: 30 }}>
              <label className="block text-sm font-medium">Fresh Water Capacity</label>
              <div className="relative flex items-center">
                <input
                  name="freshWaterCapacity"
                  type="text"
                  value={form.freshWaterCapacity}
                  onChange={(e) => handleInputChange(e, "freshWaterCapacity")}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.freshWaterCapacity ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.freshWaterCapacity ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  mt
                </span>
              </div>
              {formErrors.freshWaterCapacity && (
                <p className="mt-1 text-xs text-red-600">{formErrors.freshWaterCapacity}</p>
              )}
            </div>
            <div className="mb-4" style={{ order: 31 }}>
              <label className="block text-sm font-medium">Total Hold Capacity (Grain) in m3</label>
              <div className="relative flex items-center">
                <input
                  name="totalHoldCapacityGrainM3"
                  type="text"
                  value={form.totalHoldCapacityGrainM3}
                  onChange={(e) => handleInputChange(e, "totalHoldCapacityGrainM3")}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.totalHoldCapacityGrainM3 ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.totalHoldCapacityGrainM3 ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m3
                </span>
              </div>
              {formErrors.totalHoldCapacityGrainM3 && (
                <p className="mt-1 text-xs text-red-600">{formErrors.totalHoldCapacityGrainM3}</p>
              )}
            </div>
            <div className="mb-4" style={{ order: 32 }}>
              <label className="block text-sm font-medium">Total Hold Capacity (Bale)</label>
              <div className="relative flex items-center">
                <input
                  name="totalHoldCapacityBaleM3"
                  type="text"
                  value={form.totalHoldCapacityBaleM3}
                  onChange={(e) => handleInputChange(e, "totalHoldCapacityBaleM3")}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.totalHoldCapacityBaleM3 ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.totalHoldCapacityBaleM3 ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m3
                </span>
              </div>
              {formErrors.totalHoldCapacityBaleM3 && (
                <p className="mt-1 text-xs text-red-600">{formErrors.totalHoldCapacityBaleM3}</p>
              )}
            </div>
            <div className="mb-4" style={{ order: 27 }}>
              <label className="block text-sm font-medium">Block coeficient</label>
              <div className="relative flex items-center">
                <input
                  name="blockCoeficient"
                  type="text"
                  placeholder="blockCoeficient"
                  onChange={(e) => handleInputChange(e, "blockCoeficient")}
                  value={form.blockCoeficient}
                  className={twMerge(
                    "mt-1 w-full rounded-md border p-2 focus:outline-none",
                    formErrors.blockCoeficient ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    theme.balticQuestionnaire.grayBg,
                    formErrors.blockCoeficient ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                >
                  m
                </span>
              </div>
              <label className="block text-xs font-normal text-gray-600">Cb @ scanting draft</label>
              {formErrors.blockCoeficient && <p className="mt-1 text-xs text-red-600">{formErrors.blockCoeficient}</p>}
            </div>
            <div className="col-span-full flex flex-col gap-6" style={{ order: 40 }}>
              <p className="">
                <strong>WLTHC:</strong> Distance from waterline to top of hatch coaming or top of hatch covers if
                side-rolling hatches
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex gap-6">
                  <HeaderCell label="Condition" width="200px" />
                  <HeaderCell label="No1. Hatch" width="148px" />
                  <HeaderCell label="Midships" width="148px" />
                  <HeaderCell label="Last Hatch" width="148px" />
                </div>

                {(
                  [
                    { label: "Ballast", key: "ballast" },
                    { label: "Full ballast", key: "fullBallast" },
                    { label: "Fully laden", key: "fullyLaden" },
                  ] as const
                ).map((row) => (
                  <div className="flex items-center gap-6" key={row.key}>
                    <LabelCell label={row.label} width="200px" />
                    <InputCell
                      name={`wlthc.${row.key}.no1`}
                      error={formErrors[`wlthc.${row.key}.no1`]}
                      value={form.wlthc[row.key]?.no1 ?? ""}
                      onChange={(e) => handleInputChange(e, `wlthc.${row.key}.no1`)}
                      width="148px"
                      trailingText="m"
                      inputMode="decimal"
                    />
                    <InputCell
                      name={`wlthc.${row.key}.midships`}
                      error={formErrors[`wlthc.${row.key}.midships`]}
                      value={form.wlthc[row.key]?.midships ?? ""}
                      onChange={(e) => handleInputChange(e, `wlthc.${row.key}.midships`)}
                      width="148px"
                      trailingText="m"
                      inputMode="decimal"
                    />
                    <InputCell
                      name={`wlthc.${row.key}.last`}
                      error={formErrors[`wlthc.${row.key}.last`]}
                      value={form.wlthc[row.key]?.last ?? ""}
                      onChange={(e) => handleInputChange(e, `wlthc.${row.key}.last`)}
                      width="148px"
                      trailingText="m"
                      inputMode="decimal"
                    />
                  </div>
                ))}
                {!allWlthcFieldsDefined && (
                  <div className="pl-[230px] text-sm text-red-600">WLTHC form requires all inputs to be filled up</div>
                )}
              </div>
            </div>
            <div className="mb-4" style={{ order: 50 }}>
              <label className="block text-sm font-medium">
                Any modifications made since newbuilt that affect consumption?
              </label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    name="Anymodificationsmadesincenewbuiltthataffectconsumption"
                    type="radio"
                    className="form-radio"
                    value="yes"
                    checked={form.Anymodificationsmadesincenewbuiltthataffectconsumption === "yes"}
                    onChange={(e) => handleInputChange(e, "Anymodificationsmadesincenewbuiltthataffectconsumption")}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    name="Anymodificationsmadesincenewbuiltthataffectconsumption"
                    type="radio"
                    className="form-radio"
                    value="no"
                    checked={form.Anymodificationsmadesincenewbuiltthataffectconsumption === "no"}
                    onChange={(e) => handleInputChange(e, "Anymodificationsmadesincenewbuiltthataffectconsumption")}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.Anymodificationsmadesincenewbuiltthataffectconsumption && (
                <p className="mt-1 text-xs text-red-600">
                  {formErrors.Anymodificationsmadesincenewbuiltthataffectconsumption}
                </p>
              )}
            </div>
            {form.Anymodificationsmadesincenewbuiltthataffectconsumption === "yes" && (
              <div className="col-span-2 mb-4" style={{ order: 51 }}>
                <label className="block text-sm font-medium">Please list any</label>
                <textarea
                  name="listAny"
                  value={form.listAny}
                  onChange={(e) => handleInputChange(e, "listAny")}
                  className={twMerge(
                    "mt-1 min-h-[96px] w-full rounded-md border p-2 focus:outline-none",
                    formErrors.listAny ? "border-red-600" : theme.uwhcAssistant.border,
                  )}
                />
                {formErrors.listAny && <p className="mt-1 text-xs text-red-600">{formErrors.listAny}</p>}
              </div>
            )}
          </div>
        </form>
      </div>
    </LoadingWrapper>
  );
};
